// LearnCore Frontend - 文言文内容编辑组件

import { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  DocumentTextIcon,
  LightBulbIcon,
  AcademicCapIcon,
  BeakerIcon,
  BookmarkIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import type { ClassicalPassageWithDetails } from '../types';

interface ClassicalContentEditorProps {
  passage: ClassicalPassageWithDetails;
  passageId: string;
  onUpdate: () => void; // 更新后回调
}

export default function ClassicalContentEditor({ passage, passageId, onUpdate }: ClassicalContentEditorProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [editingSections, setEditingSections] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // 编辑状态的副本
  const [editData, setEditData] = useState<ClassicalPassageWithDetails>(passage);

  // 当passage数据更新时，同步更新editData
  useEffect(() => {
    setEditData(passage);
  }, [passage]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newExpanded = new Set(prev);
      
      // 如果点击的是已展开的栏目，则关闭它
      if (prev.has(sectionId)) {
        newExpanded.delete(sectionId);
      } else {
        // 否则展开当前点击的栏目
        newExpanded.add(sectionId);
      }
      
      return newExpanded;
    });
  };

  const toggleEdit = (sectionId: string) => {
    setEditingSections(prev => {
      const newEditing = new Set(prev);
      if (newEditing.has(sectionId)) {
        newEditing.delete(sectionId);
      } else {
        newEditing.add(sectionId);
      }
      return newEditing;
    });
    
    // 确保编辑时展开对应区域
    setExpandedSections(prev => {
      const newExpanded = new Set(prev);
      newExpanded.add(sectionId);
      return newExpanded;
    });
  };

  const handleSave = async (sectionId: string) => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      let response;
      switch (sectionId) {
        case 'overview':
          // 保存概览部分的所有内容
          await apiClient.updateClassicalTranslations(passageId, {
            translations: editData.translations
          });
          await apiClient.updateClassicalMainIdeas(passageId, {
            main_ideas: editData.main_ideas
          });
          response = { message: '核心概览保存成功' };
          break;
        case 'translations':
          response = await apiClient.updateClassicalTranslations(passageId, {
            translations: editData.translations
          });
          break;
        case 'main-ideas':
          response = await apiClient.updateClassicalMainIdeas(passageId, {
            main_ideas: editData.main_ideas
          });
          break;
        case 'words':
          // 保存字词精讲部分的所有内容
          await apiClient.updateClassicalKeyWords(passageId, {
            key_words: editData.key_words
          });
          await apiClient.updateClassicalFunctionWords(passageId, {
            function_words: editData.function_words
          });
          await apiClient.updateClassicalSpecialChars(passageId, {
            special_chars: editData.special_chars
          });
          response = { message: '字词精讲保存成功' };
          break;
        case 'key-words':
          response = await apiClient.updateClassicalKeyWords(passageId, {
            key_words: editData.key_words
          });
          break;
        case 'function-words':
          response = await apiClient.updateClassicalFunctionWords(passageId, {
            function_words: editData.function_words
          });
          break;
        case 'special-chars':
          response = await apiClient.updateClassicalSpecialChars(passageId, {
            special_chars: editData.special_chars
          });
          break;
        case 'grammar':
          // 保存语法与句式部分的所有内容
          await apiClient.updateClassicalSentencePatterns(passageId, {
            sentence_patterns: editData.sentence_patterns
          });
          await apiClient.updateClassicalWordUsage(passageId, {
            word_usage: editData.word_usage
          });
          response = { message: '语法与句式保存成功' };
          break;
        case 'sentence-patterns':
          response = await apiClient.updateClassicalSentencePatterns(passageId, {
            sentence_patterns: editData.sentence_patterns
          });
          break;
        case 'word-usage':
          response = await apiClient.updateClassicalWordUsage(passageId, {
            word_usage: editData.word_usage
          });
          break;
        case 'literature':
          // 保存文学与文化部分的所有内容
          await apiClient.updateClassicalRhetoric(passageId, {
            rhetoric: editData.rhetoric
          });
          await apiClient.updateClassicalLiteraryKnowledge(passageId, {
            literary_knowledge: editData.literary_knowledge
          });
          await apiClient.updateClassicalCharacterAnalysis(passageId, {
            character_analysis: editData.character_analysis
          });
          response = { message: '文学与文化保存成功' };
          break;
        case 'rhetoric':
          response = await apiClient.updateClassicalRhetoric(passageId, {
            rhetoric: editData.rhetoric
          });
          break;
        case 'literary-knowledge':
          response = await apiClient.updateClassicalLiteraryKnowledge(passageId, {
            literary_knowledge: editData.literary_knowledge
          });
          break;
        case 'character-analysis':
          response = await apiClient.updateClassicalCharacterAnalysis(passageId, {
            character_analysis: editData.character_analysis
          });
          break;
        case 'learning':
          // 保存学习与拓展部分的所有内容
          await apiClient.updateClassicalWritingFeatures(passageId, {
            writing_features: editData.writing_features
          });
          await apiClient.updateClassicalInsights(passageId, {
            insights: editData.insights
          });
          response = { message: '学习与拓展保存成功' };
          break;
        case 'writing-features':
          response = await apiClient.updateClassicalWritingFeatures(passageId, {
            writing_features: editData.writing_features
          });
          break;
        case 'insights':
          response = await apiClient.updateClassicalInsights(passageId, {
            insights: editData.insights
          });
          break;
        default:
          throw new Error('未知的编辑部分');
      }

      if (response.error) {
        throw new Error(response.error);
      }

      setSuccessMessage('保存成功');
      setEditingSections(prev => {
        const newEditing = new Set(prev);
        newEditing.delete(sectionId);
        return newEditing;
      });
      
      // 通知父组件更新
      onUpdate();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || '保存失败');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (sectionId: string) => {
    // 恢复原始数据
    setEditData(passage);
    setEditingSections(prev => {
      const newEditing = new Set(prev);
      newEditing.delete(sectionId);
      return newEditing;
    });
  };

  const updateField = (section: string, index: number, field: string, value: string) => {
    setEditData(prev => {
      const newData = { ...prev } as any;
      const sectionData = newData[section] || [];
      if (sectionData && sectionData[index]) {
        sectionData[index] = {
          ...sectionData[index],
          [field]: value
        };
      }
      return newData;
    });
  };

  const addItem = (section: string) => {
    setEditData(prev => {
      const newData = { ...prev } as any;
      const sectionData = newData[section] || [];
      
      let newItem;
      switch (section) {
        case 'translations':
          newItem = { id: '', translation: '', paragraph_index: 0, sort_order: sectionData.length };
          break;
        case 'main_ideas':
          newItem = { id: '', main_idea: '', emotion: '', paragraph_index: 0, sort_order: sectionData.length };
          break;
        case 'key_words':
          newItem = { id: '', word: '', meaning_in_text: '', multiple_meanings: '', ancient_modern_diff: '', example_sentence: '', paragraph_index: 0, sort_order: sectionData.length };
          break;
        case 'function_words':
          newItem = { id: '', word: '', word_class: '', function_description: '', example_sentence: '', paragraph_index: 0, sort_order: sectionData.length };
          break;
        case 'special_chars':
          newItem = { id: '', char_type: 'tongjiazi', original_char: '', correct_char: '', pronunciation: '', ancient_meaning: '', modern_meaning: '', example_sentence: '', paragraph_index: 0, sort_order: sectionData.length };
          break;
        case 'sentence_patterns':
          newItem = { id: '', pattern_type: '', pattern_name: '', explanation: '', example_sentence: '', normal_order: '', paragraph_index: 0, sort_order: sectionData.length };
          break;
        case 'word_usage':
          newItem = { id: '', usage_type: '', word: '', explanation: '', example_sentence: '', paragraph_index: 0, sort_order: sectionData.length };
          break;
        case 'rhetoric':
          newItem = { id: '', rhetoric_type: '', explanation: '', example_sentence: '', effect_description: '', paragraph_index: 0, sort_order: sectionData.length };
          break;
        case 'literary_knowledge':
          newItem = { id: '', knowledge_type: 'source', title: '', content: '', paragraph_index: 0, sort_order: sectionData.length };
          break;
        case 'character_analysis':
          newItem = { id: '', character_name: '', personality_traits: '', analysis: '', supporting_evidence: '', paragraph_index: 0, sort_order: sectionData.length };
          break;
        case 'writing_features':
          newItem = { id: '', feature_type: '', title: '', description: '', paragraph_index: 0, sort_order: sectionData.length };
          break;
        case 'insights':
          newItem = { id: '', insight: '', modern_significance: '', paragraph_index: 0, sort_order: sectionData.length };
          break;
        default:
          return prev;
      }
      
      newData[section] = [...sectionData, newItem];
      return newData;
    });
  };

  const removeItem = (section: string, index: number) => {
    setEditData(prev => {
      const newData = { ...prev } as any;
      const sectionData = newData[section] || [];
      newData[section] = sectionData.filter((_: any, i: number) => i !== index);
      return newData;
    });
  };

  // 只有在提取完成后才显示编辑器
  if (passage.processing_status !== 'completed') {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* 成功消息 */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <CheckIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XMarkIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">操作失败</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <div className="mt-4">
                <button
                  onClick={() => setError(null)}
                  className="btn-outline text-sm"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 左侧：原文展示 + 核心概览 */}
        <div className="space-y-6">
          {/* 原文展示 */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <DocumentTextIcon className="h-5 w-5 mr-2 text-amber-600" />
              文言文原文
            </h2>
            <div className="prose prose-sm max-w-none">
              {passage.original_text}
            </div>
          </div>

          {/* 核心概览 */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <LightBulbIcon className="h-5 w-5 mr-3 text-amber-600" />
                <span className="font-medium text-gray-900">核心概览</span>
              </div>
              <div className="flex items-center gap-2">
                {editingSections.has('overview') ? (
                  <>
                    <button
                      onClick={() => handleSave('overview')}
                      disabled={saving}
                      className="btn-primary text-sm"
                    >
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={() => handleCancel('overview')}
                      disabled={saving}
                      className="btn-outline text-sm"
                    >
                      取消
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => toggleEdit('overview')}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                    编辑
                  </button>
                )}
                <button
                  onClick={() => toggleSection('overview')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {expandedSections.has('overview') ? (
                    <ChevronDownIcon className="h-5 w-5" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {expandedSections.has('overview') && (
              <div className="px-6 pb-6 space-y-4">
                {/* 译文编辑 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">全文译文</h4>
                  {editData.translations.map((translation, index) => (
                    <div key={index} className="mb-3">
                      {editingSections.has('overview') ? (
                        <div className="space-y-2">
                          <textarea
                            value={translation.translation}
                            onChange={(e) => updateField('translations', index, 'translation', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={4}
                            placeholder="输入译文..."
                          />
                          <button
                            onClick={() => removeItem('translations', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        </div>
                      ) : (
                        <div className="text-gray-700 bg-gray-50 p-3 rounded-md">
                          {translation.translation}
                        </div>
                      )}
                    </div>
                  ))}
                  {editingSections.has('overview') && (
                    <button
                      onClick={() => addItem('translations')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      + 添加译文
                    </button>
                  )}
                </div>

                {/* 主旨思想编辑 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">主旨思想</h4>
                  {editData.main_ideas.map((idea, index) => (
                    <div key={index} className="mb-3 space-y-2">
                      {editingSections.has('overview') ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={idea.main_idea}
                            onChange={(e) => updateField('main_ideas', index, 'main_idea', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入主旨思想..."
                          />
                          <input
                            type="text"
                            value={idea.emotion || ''}
                            onChange={(e) => updateField('main_ideas', index, 'emotion', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入情感..."
                          />
                          <button
                            onClick={() => removeItem('main_ideas', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="text-gray-700">{idea.main_idea}</div>
                          {idea.emotion && (
                            <div className="text-sm text-gray-600">
                              <strong>情感：</strong>{idea.emotion}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                  {editingSections.has('overview') && (
                    <button
                      onClick={() => addItem('main_ideas')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      + 添加主旨思想
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 右侧：其他4个栏目 */}
        <div className="space-y-6">
          {/* 字词精讲 */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <AcademicCapIcon className="h-5 w-5 mr-3 text-blue-600" />
                <span className="font-medium text-gray-900">字词精讲</span>
                <span className="ml-2 text-sm text-gray-500">
                  ({editData.key_words.length + editData.function_words.length + editData.special_chars.length} 个)
                </span>
              </div>
              <div className="flex items-center gap-2">
                {editingSections.has('words') ? (
                  <>
                    <button
                      onClick={() => handleSave('words')}
                      disabled={saving}
                      className="btn-primary text-sm"
                    >
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={() => handleCancel('words')}
                      disabled={saving}
                      className="btn-outline text-sm"
                    >
                      取消
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => toggleEdit('words')}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                    编辑
                  </button>
                )}
                <button
                  onClick={() => toggleSection('words')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {expandedSections.has('words') ? (
                    <ChevronDownIcon className="h-5 w-5" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {expandedSections.has('words') && (
              <div className="px-6 pb-6 space-y-4">
                {/* 重点实词 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">重点实词</h4>
                  {editData.key_words.map((word, index) => (
                    <div key={index} className="mb-3 space-y-2">
                      {editingSections.has('words') ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={word.word}
                            onChange={(e) => updateField('key_words', index, 'word', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入词语..."
                          />
                          <textarea
                            value={word.meaning_in_text}
                            onChange={(e) => updateField('key_words', index, 'meaning_in_text', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入在文中的含义..."
                          />
                          <textarea
                            value={word.multiple_meanings || ''}
                            onChange={(e) => updateField('key_words', index, 'multiple_meanings', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入一词多义..."
                          />
                          <textarea
                            value={word.ancient_modern_diff || ''}
                            onChange={(e) => updateField('key_words', index, 'ancient_modern_diff', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入古今异义..."
                          />
                          <button
                            onClick={() => removeItem('key_words', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        </div>
                      ) : (
                        <div className="border-l-4 border-blue-200 pl-3">
                          <div className="font-medium text-gray-900">{word.word}</div>
                          <div className="text-sm text-gray-700">{word.meaning_in_text}</div>
                          {word.multiple_meanings && (
                            <div className="text-xs text-gray-600 mt-1">
                              <strong>一词多义：</strong>{word.multiple_meanings}
                            </div>
                          )}
                          {word.ancient_modern_diff && (
                            <div className="text-xs text-gray-600 mt-1">
                              <strong>古今异义：</strong>{word.ancient_modern_diff}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {editingSections.has('words') && (
                    <button
                      onClick={() => addItem('key_words')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      + 添加重点实词
                    </button>
                  )}
                </div>

                {/* 核心虚词 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">核心虚词</h4>
                  {editData.function_words.map((word, index) => (
                    <div key={index} className="mb-3 space-y-2">
                      {editingSections.has('words') ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={word.word}
                            onChange={(e) => updateField('function_words', index, 'word', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入虚词..."
                          />
                          <input
                            type="text"
                            value={word.word_class}
                            onChange={(e) => updateField('function_words', index, 'word_class', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入词类..."
                          />
                          <textarea
                            value={word.function_description}
                            onChange={(e) => updateField('function_words', index, 'function_description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入功能描述..."
                          />
                          <button
                            onClick={() => removeItem('function_words', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        </div>
                      ) : (
                        <div className="border-l-4 border-green-200 pl-3">
                          <div className="font-medium text-gray-900">{word.word}</div>
                          <div className="text-sm text-gray-700">
                            <span className="text-green-600">{word.word_class}</span> - {word.function_description}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {editingSections.has('words') && (
                    <button
                      onClick={() => addItem('function_words')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      + 添加核心虚词
                    </button>
                  )}
                </div>

                {/* 通假字与古今异义 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">通假字与古今异义</h4>
                  {editData.special_chars.map((char, index) => (
                    <div key={index} className="mb-3 space-y-2">
                      {editingSections.has('words') ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={char.original_char}
                            onChange={(e) => updateField('special_chars', index, 'original_char', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入原字..."
                          />
                          <select
                            value={char.char_type}
                            onChange={(e) => updateField('special_chars', index, 'char_type', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                          >
                            <option value="tongjiazi">通假字</option>
                            <option value="gujinyiyi">古今异义</option>
                          </select>
                          <input
                            type="text"
                            value={char.correct_char || ''}
                            onChange={(e) => updateField('special_chars', index, 'correct_char', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入本字..."
                          />
                          <textarea
                            value={char.ancient_meaning || ''}
                            onChange={(e) => updateField('special_chars', index, 'ancient_meaning', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入古义..."
                          />
                          <textarea
                            value={char.modern_meaning || ''}
                            onChange={(e) => updateField('special_chars', index, 'modern_meaning', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入今义..."
                          />
                          <button
                            onClick={() => removeItem('special_chars', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        </div>
                      ) : (
                        <div className="border-l-4 border-orange-200 pl-3">
                          <div className="font-medium text-gray-900">{char.original_char}</div>
                          <div className="text-sm text-gray-700">
                            {char.char_type === 'tongjiazi' ? '通假字' : '古今异义'}
                            {char.correct_char && ` (本字: ${char.correct_char})`}
                          </div>
                          {char.ancient_meaning && (
                            <div className="text-xs text-gray-600 mt-1">
                              <strong>古义：</strong>{char.ancient_meaning}
                            </div>
                          )}
                          {char.modern_meaning && (
                            <div className="text-xs text-gray-600 mt-1">
                              <strong>今义：</strong>{char.modern_meaning}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {editingSections.has('words') && (
                    <button
                      onClick={() => addItem('special_chars')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      + 添加通假字与古今异义
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 语法与句式 */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <BeakerIcon className="h-5 w-5 mr-3 text-purple-600" />
                <span className="font-medium text-gray-900">语法与句式</span>
                <span className="ml-2 text-sm text-gray-500">
                  ({editData.sentence_patterns.length + editData.word_usage.length} 个)
                </span>
              </div>
              <div className="flex items-center gap-2">
                {editingSections.has('grammar') ? (
                  <>
                    <button
                      onClick={() => handleSave('grammar')}
                      disabled={saving}
                      className="btn-primary text-sm"
                    >
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={() => handleCancel('grammar')}
                      disabled={saving}
                      className="btn-outline text-sm"
                    >
                      取消
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => toggleEdit('grammar')}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                    编辑
                  </button>
                )}
                <button
                  onClick={() => toggleSection('grammar')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {expandedSections.has('grammar') ? (
                    <ChevronDownIcon className="h-5 w-5" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {expandedSections.has('grammar') && (
              <div className="px-6 pb-6 space-y-4">
                {/* 特殊句式 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">特殊句式</h4>
                  {editData.sentence_patterns.map((pattern, index) => (
                    <div key={index} className="mb-3 space-y-2">
                      {editingSections.has('grammar') ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={pattern.pattern_name}
                            onChange={(e) => updateField('sentence_patterns', index, 'pattern_name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入句式名称..."
                          />
                          <textarea
                            value={pattern.explanation}
                            onChange={(e) => updateField('sentence_patterns', index, 'explanation', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入解释..."
                          />
                          <textarea
                            value={pattern.example_sentence}
                            onChange={(e) => updateField('sentence_patterns', index, 'example_sentence', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入例句..."
                          />
                          <textarea
                            value={pattern.normal_order || ''}
                            onChange={(e) => updateField('sentence_patterns', index, 'normal_order', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入正常语序..."
                          />
                          <button
                            onClick={() => removeItem('sentence_patterns', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        </div>
                      ) : (
                        <div className="border-l-4 border-purple-200 pl-3">
                          <div className="font-medium text-gray-900">{pattern.pattern_name}</div>
                          <div className="text-sm text-gray-700 mb-2">{pattern.explanation}</div>
                          <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                            <strong>例句：</strong>{pattern.example_sentence}
                          </div>
                          {pattern.normal_order && (
                            <div className="text-xs text-gray-600 mt-1">
                              <strong>正常语序：</strong>{pattern.normal_order}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {editingSections.has('grammar') && (
                    <button
                      onClick={() => addItem('sentence_patterns')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      + 添加特殊句式
                    </button>
                  )}
                </div>

                {/* 词类活用 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">词类活用</h4>
                  {editData.word_usage.map((usage, index) => (
                    <div key={index} className="mb-3 space-y-2">
                      {editingSections.has('grammar') ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={usage.word}
                            onChange={(e) => updateField('word_usage', index, 'word', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入词语..."
                          />
                          <input
                            type="text"
                            value={usage.usage_type}
                            onChange={(e) => updateField('word_usage', index, 'usage_type', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入活用类型..."
                          />
                          <textarea
                            value={usage.explanation}
                            onChange={(e) => updateField('word_usage', index, 'explanation', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入解释..."
                          />
                          <textarea
                            value={usage.example_sentence}
                            onChange={(e) => updateField('word_usage', index, 'example_sentence', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入例句..."
                          />
                          <button
                            onClick={() => removeItem('word_usage', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        </div>
                      ) : (
                        <div className="border-l-4 border-indigo-200 pl-3">
                          <div className="font-medium text-gray-900">{usage.word}</div>
                          <div className="text-sm text-gray-700">
                            <span className="text-indigo-600">{usage.usage_type}</span> - {usage.explanation}
                          </div>
                          <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded mt-1">
                            <strong>例句：</strong>{usage.example_sentence}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {editingSections.has('grammar') && (
                    <button
                      onClick={() => addItem('word_usage')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      + 添加词类活用
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 文学与文化 */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <BookmarkIcon className="h-5 w-5 mr-3 text-red-600" />
                <span className="font-medium text-gray-900">文学与文化</span>
                <span className="ml-2 text-sm text-gray-500">
                  ({editData.rhetoric.length + editData.literary_knowledge.length + editData.character_analysis.length} 个)
                </span>
              </div>
              <div className="flex items-center gap-2">
                {editingSections.has('literature') ? (
                  <>
                    <button
                      onClick={() => handleSave('literature')}
                      disabled={saving}
                      className="btn-primary text-sm"
                    >
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={() => handleCancel('literature')}
                      disabled={saving}
                      className="btn-outline text-sm"
                    >
                      取消
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => toggleEdit('literature')}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                    编辑
                  </button>
                )}
                <button
                  onClick={() => toggleSection('literature')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {expandedSections.has('literature') ? (
                    <ChevronDownIcon className="h-5 w-5" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {expandedSections.has('literature') && (
              <div className="px-6 pb-6 space-y-4">
                {/* 修辞手法 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">修辞手法</h4>
                  {editData.rhetoric.map((rhetoric, index) => (
                    <div key={index} className="mb-3 space-y-2">
                      {editingSections.has('literature') ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={rhetoric.rhetoric_type}
                            onChange={(e) => updateField('rhetoric', index, 'rhetoric_type', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入修辞类型..."
                          />
                          <textarea
                            value={rhetoric.explanation}
                            onChange={(e) => updateField('rhetoric', index, 'explanation', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入解释..."
                          />
                          <textarea
                            value={rhetoric.example_sentence}
                            onChange={(e) => updateField('rhetoric', index, 'example_sentence', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入例句..."
                          />
                          <textarea
                            value={rhetoric.effect_description || ''}
                            onChange={(e) => updateField('rhetoric', index, 'effect_description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入表达效果..."
                          />
                          <button
                            onClick={() => removeItem('rhetoric', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        </div>
                      ) : (
                        <div className="border-l-4 border-red-200 pl-3">
                          <div className="font-medium text-gray-900">{rhetoric.rhetoric_type}</div>
                          <div className="text-sm text-gray-700 mb-2">{rhetoric.explanation}</div>
                          <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                            <strong>例句：</strong>{rhetoric.example_sentence}
                          </div>
                          {rhetoric.effect_description && (
                            <div className="text-xs text-gray-600 mt-1">
                              <strong>表达效果：</strong>{rhetoric.effect_description}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {editingSections.has('literature') && (
                    <button
                      onClick={() => addItem('rhetoric')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      + 添加修辞手法
                    </button>
                  )}
                </div>

                {/* 文学常识 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">文学常识</h4>
                  {editData.literary_knowledge.map((knowledge, index) => (
                    <div key={index} className="mb-3 space-y-2">
                      {editingSections.has('literature') ? (
                        <div className="space-y-2">
                          <select
                            value={knowledge.knowledge_type}
                            onChange={(e) => updateField('literary_knowledge', index, 'knowledge_type', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                          >
                            <option value="source">出处</option>
                            <option value="author">作者</option>
                            <option value="allusion">典故</option>
                            <option value="background">背景</option>
                          </select>
                          <input
                            type="text"
                            value={knowledge.title}
                            onChange={(e) => updateField('literary_knowledge', index, 'title', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入标题..."
                          />
                          <textarea
                            value={knowledge.content}
                            onChange={(e) => updateField('literary_knowledge', index, 'content', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={3}
                            placeholder="输入内容..."
                          />
                          <button
                            onClick={() => removeItem('literary_knowledge', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        </div>
                      ) : (
                        <div className="border-l-4 border-yellow-200 pl-3">
                          <div className="font-medium text-gray-900">{knowledge.title}</div>
                          <div className="text-sm text-gray-700">{knowledge.content}</div>
                        </div>
                      )}
                    </div>
                  ))}
                  {editingSections.has('literature') && (
                    <button
                      onClick={() => addItem('literary_knowledge')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      + 添加文学常识
                    </button>
                  )}
                </div>

                {/* 人物形象分析 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">人物形象分析</h4>
                  {editData.character_analysis.map((character, index) => (
                    <div key={index} className="mb-3 space-y-2">
                      {editingSections.has('literature') ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={character.character_name}
                            onChange={(e) => updateField('character_analysis', index, 'character_name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入人物名称..."
                          />
                          <textarea
                            value={character.personality_traits}
                            onChange={(e) => updateField('character_analysis', index, 'personality_traits', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入性格特点..."
                          />
                          <textarea
                            value={character.analysis}
                            onChange={(e) => updateField('character_analysis', index, 'analysis', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={3}
                            placeholder="输入分析..."
                          />
                          <textarea
                            value={character.supporting_evidence || ''}
                            onChange={(e) => updateField('character_analysis', index, 'supporting_evidence', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入支撑证据..."
                          />
                          <button
                            onClick={() => removeItem('character_analysis', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        </div>
                      ) : (
                        <div className="border-l-4 border-pink-200 pl-3">
                          <div className="font-medium text-gray-900">{character.character_name}</div>
                          <div className="text-sm text-gray-700 mb-1">
                            <strong>性格特点：</strong>{character.personality_traits}
                          </div>
                          <div className="text-sm text-gray-700 mb-1">{character.analysis}</div>
                          {character.supporting_evidence && (
                            <div className="text-xs text-gray-600">
                              <strong>支撑证据：</strong>{character.supporting_evidence}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {editingSections.has('literature') && (
                    <button
                      onClick={() => addItem('character_analysis')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      + 添加人物形象分析
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 学习与拓展 */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <StarIcon className="h-5 w-5 mr-3 text-green-600" />
                <span className="font-medium text-gray-900">学习与拓展</span>
                <span className="ml-2 text-sm text-gray-500">
                  ({editData.writing_features.length + editData.insights.length} 个)
                </span>
              </div>
              <div className="flex items-center gap-2">
                {editingSections.has('learning') ? (
                  <>
                    <button
                      onClick={() => handleSave('learning')}
                      disabled={saving}
                      className="btn-primary text-sm"
                    >
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={() => handleCancel('learning')}
                      disabled={saving}
                      className="btn-outline text-sm"
                    >
                      取消
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => toggleEdit('learning')}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                    编辑
                  </button>
                )}
                <button
                  onClick={() => toggleSection('learning')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {expandedSections.has('learning') ? (
                    <ChevronDownIcon className="h-5 w-5" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {expandedSections.has('learning') && (
              <div className="px-6 pb-6 space-y-4">
                {/* 写作特色 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">写作特色</h4>
                  {editData.writing_features.map((feature, index) => (
                    <div key={index} className="mb-3 space-y-2">
                      {editingSections.has('learning') ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={feature.feature_type}
                            onChange={(e) => updateField('writing_features', index, 'feature_type', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入特色类型..."
                          />
                          <input
                            type="text"
                            value={feature.title}
                            onChange={(e) => updateField('writing_features', index, 'title', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="输入标题..."
                          />
                          <textarea
                            value={feature.description}
                            onChange={(e) => updateField('writing_features', index, 'description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={3}
                            placeholder="输入描述..."
                          />
                          <button
                            onClick={() => removeItem('writing_features', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        </div>
                      ) : (
                        <div className="border-l-4 border-green-200 pl-3">
                          <div className="font-medium text-gray-900">{feature.title}</div>
                          <div className="text-sm text-gray-700">{feature.description}</div>
                        </div>
                      )}
                    </div>
                  ))}
                  {editingSections.has('learning') && (
                    <button
                      onClick={() => addItem('writing_features')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      + 添加写作特色
                    </button>
                  )}
                </div>

                {/* 启示与思考 */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">启示与思考</h4>
                  {editData.insights.map((insight, index) => (
                    <div key={index} className="mb-3 space-y-2">
                      {editingSections.has('learning') ? (
                        <div className="space-y-2">
                          <textarea
                            value={insight.insight}
                            onChange={(e) => updateField('insights', index, 'insight', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={3}
                            placeholder="输入启示..."
                          />
                          <textarea
                            value={insight.modern_significance || ''}
                            onChange={(e) => updateField('insights', index, 'modern_significance', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            rows={2}
                            placeholder="输入现代意义..."
                          />
                          <button
                            onClick={() => removeItem('insights', index)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            删除
                          </button>
                        </div>
                      ) : (
                        <div className="border-l-4 border-emerald-200 pl-3">
                          <div className="text-sm text-gray-700 mb-1">{insight.insight}</div>
                          {insight.modern_significance && (
                            <div className="text-xs text-gray-600">
                              <strong>现代意义：</strong>{insight.modern_significance}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {editingSections.has('learning') && (
                    <button
                      onClick={() => addItem('insights')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      + 添加启示与思考
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}